import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useValidationCounts } from '../features/validation/validationCounts';
import {
  FileText,
  FlaskConical,
  Database,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Home,
  LogOut,
  HardDrive,
  Settings,
  Users,
  History,
  ShieldCheck,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo, useBranding } from '../contexts/BrandingContext';
import StudySetupPanel from './StudySetupPanel';
import { useProjectMembership } from '../hooks/useProjectMembership';

interface WorkflowPhase {
  id: string;
  name: string;
  shortName: string;
  icon: React.ReactNode;
  path: string;
  description: string;
  accentColor: string;       // Tailwind color for active state
  accentBg: string;          // Light background
  accentText: string;        // Text color
  accentBorder: string;      // Border/indicator color
}

interface WorkflowLayoutProps {
  children: React.ReactNode;
  projectId: string;
  projectName: string;
}

const workflowPhases: WorkflowPhase[] = [
  {
    id: 'protocol',
    name: 'Protocol Intelligence',
    shortName: 'Protocol',
    icon: <FileText className="h-5 w-5" />,
    path: 'protocol',
    description: 'Upload and analyze protocol',
    accentColor: 'indigo',
    accentBg: 'bg-indigo-500/10',
    accentText: 'text-indigo-400',
    accentBorder: 'border-indigo-400',
  },
  {
    id: 'study-design',
    name: 'Study Design',
    shortName: 'Design',
    icon: <FlaskConical className="h-5 w-5" />,
    path: 'study-design',
    description: 'Configure study parameters',
    accentColor: 'violet',
    accentBg: 'bg-violet-500/10',
    accentText: 'text-violet-400',
    accentBorder: 'border-violet-400',
  },
  {
    id: 'crf-builder',
    name: 'CRF Builder',
    shortName: 'CRF',
    icon: <FileText className="h-5 w-5" />,
    path: 'crf-builder',
    description: 'Design case report forms',
    accentColor: 'blue',
    accentBg: 'bg-blue-500/10',
    accentText: 'text-blue-400',
    accentBorder: 'border-blue-400',
  },
  {
    id: 'data-management',
    name: 'Study Documents',
    shortName: 'Docs',
    icon: <Database className="h-5 w-5" />,
    path: 'data-management',
    description: 'Generate study deliverables',
    accentColor: 'amber',
    accentBg: 'bg-amber-500/10',
    accentText: 'text-amber-400',
    accentBorder: 'border-amber-400',
  },
  {
    // id/path stay 'validation' (route + internal identifiers unchanged); only
    // the user-facing label is "Verification".
    id: 'validation',
    name: 'Verification',
    shortName: 'Verification',
    icon: <ShieldCheck className="h-5 w-5" />,
    path: 'validation',
    description: 'Review automatic checks across your study documents',
    accentColor: 'rose',
    accentBg: 'bg-rose-500/10',
    accentText: 'text-rose-400',
    accentBorder: 'border-rose-400',
  },
  {
    id: 'edc-build',
    name: 'Study Build',
    shortName: 'Study Build',
    icon: <HardDrive className="h-5 w-5" />,
    path: 'database-schema',
    description: 'Validate the study configuration in your EDC',
    accentColor: 'teal',
    accentBg: 'bg-teal-500/10',
    accentText: 'text-teal-400',
    accentBorder: 'border-teal-400',
  },
  {
    id: 'documents',
    name: 'Documents & TMF',
    shortName: 'Documents',
    icon: <FolderOpen className="h-5 w-5" />,
    path: 'documents',
    description: 'Trial master file and documents',
    accentColor: 'emerald',
    accentBg: 'bg-emerald-500/10',
    accentText: 'text-emerald-400',
    accentBorder: 'border-emerald-400',
  }
];

export default function WorkflowLayout({
  children,
  projectId,
  projectName,
}: WorkflowLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { branding } = useBranding();
  // Sidebar visibility: Team is gated by manage_members (membership
  // edit power); Activity by the dedicated view_activity privilege so
  // the Owner can grant audit-log access without promoting someone to
  // Manager. Defaults: both Owner and Manager have both; Editors /
  // Reviewers / Viewers have neither.
  const { has: hasPriv } = useProjectMembership(projectId);
  const canManage = hasPriv('manage_members');
  const canViewActivity = hasPriv('view_activity');

  const currentPhase = workflowPhases.find(phase =>
    location.pathname.includes(`/project/${projectId}/${phase.path}`)
  ) || workflowPhases[0];

  // Help is a reference page, not a workflow phase, so it isn't in
  // workflowPhases and currentPhase falls back to the first phase. Override the
  // breadcrumb title here so the header doesn't claim you're on Protocol
  // Intelligence while you're reading the help guide.
  const onHelp = location.pathname.endsWith('/help');
  const headerTitle = onHelp ? 'Help' : currentPhase.name;

  // Ambient verification status — a small chip on the Verification nav item so
  // the "is my build OK?" signal is visible from every step. It shows the SAME
  // number the Verification page headline shows (items needing a decision),
  // derived from the SAME shared source, so the two can never disagree. Calm
  // styling: a low-key count when there is anything to review, a green check when
  // clear — not an alarm.
  const valCounts = useValidationCounts(projectId);

  const handlePhaseClick = (phase: WorkflowPhase) => {
    navigate(`/project/${projectId}/${phase.path}`);
  };

  const handleLogout = async () => {
    if (await logout()) navigate('/login');
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar - Dark gradient with phase colors */}
      <div
        className={`${
          sidebarCollapsed ? 'w-16' : 'w-64'
        } bg-gradient-to-b from-[#001820] via-[#00262D] to-[#001820] flex flex-col transition-all duration-300 shadow-sidebar`}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4">
          {!sidebarCollapsed ? (
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2.5 hover:bg-white/5 px-2 py-1 -mx-2 rounded-lg transition-colors group"
              title="Go to Dashboard"
            >
              <BrandLogo className="h-8 w-8 rounded-lg object-contain" />
              <span className="font-bold text-white tracking-tight">{branding.productName}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="h-8 w-8 rounded-lg transition-opacity hover:opacity-80"
              title="Go to Dashboard"
            >
              <BrandLogo className="h-8 w-8 rounded-lg object-contain" />
            </button>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Project Name */}
        {!sidebarCollapsed && (
          <div className="px-4 py-3 border-b border-white/5 mx-3 mb-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-medium">Project</p>
            <p className="font-medium text-slate-200 truncate text-sm mt-0.5" title={projectName}>{projectName}</p>
          </div>
        )}

        {/* Navigation with progress connector */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {!sidebarCollapsed && (
            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-medium px-6 mb-3">
              Workflow
            </p>
          )}

          <div className="relative px-3">
            {workflowPhases.map((phase) => {
              const isActive = currentPhase.id === phase.id;
              // Ambient validation chip — only on the Validation nav item, only
              // once a run exists. Amber (with the count needing a decision) when
              // review is needed; green check when clean.
              const showVal = phase.id === 'validation' && valCounts.exists;
              // The SAME total the Verification page headline shows. Calm styling:
              // a low-key slate count when anything awaits review, a green check
              // when clear — not an alarm.
              const hasItems = showVal && valCounts.active > 0;
              const valDotClass = !showVal ? '' : hasItems ? 'bg-slate-400' : 'bg-emerald-400';
              const valTip = !showVal ? ''
                : hasItems ? `${valCounts.active} item${valCounts.active === 1 ? '' : 's'} to review`
                : 'all clear';

              return (
                <button
                  key={phase.id}
                  onClick={() => handlePhaseClick(phase)}
                  className={`w-full flex items-center px-3 py-2.5 mb-0.5 rounded-lg transition-all duration-150 relative ${
                    isActive
                      ? 'bg-white/10 border-r-2 border-[#D89A32]'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  }`}
                  title={sidebarCollapsed
                    ? (showVal ? `${phase.name} — ${valTip}` : phase.name)
                    : undefined}
                >
                  <div className="flex items-center space-x-3 w-full">
                    {/* Phase icon */}
                    <div className={`flex-shrink-0 relative z-10 ${
                      isActive ? 'text-[#79D1C7]' : 'text-slate-500'
                    }`}>
                      {phase.icon}
                      {/* Collapsed-sidebar status dot */}
                      {showVal && sidebarCollapsed && (
                        <span className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-slate-900 ${valDotClass}`} />
                      )}
                    </div>

                    {!sidebarCollapsed && (
                      <div className="flex-1 text-left min-w-0">
                        <span className={`font-medium text-sm block truncate ${
                          isActive ? 'text-white' : 'text-slate-300'
                        }`}>
                          {phase.name}
                        </span>
                        <p className={`text-xs truncate ${
                          isActive ? 'text-slate-400' : 'text-slate-500'
                        }`}>{phase.description}</p>
                      </div>
                    )}

                    {/* Expanded-sidebar status chip — total to review (calm) or clear */}
                    {showVal && !sidebarCollapsed && hasItems && (
                      <span title={valTip} className="flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-medium flex items-center justify-center bg-slate-500/20 text-slate-300 ring-1 ring-slate-400/25">
                        {valCounts.active}
                      </span>
                    )}
                    {showVal && !sidebarCollapsed && !hasItems && (
                      <span title="All clear" className="flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30 text-[11px] font-bold">✓</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="border-t border-white/5 p-3 space-y-0.5">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center px-3 py-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 rounded-lg transition-colors"
            title={sidebarCollapsed ? 'Dashboard' : undefined}
          >
            <Home className="h-5 w-5" />
            {!sidebarCollapsed && <span className="ml-3">Dashboard</span>}
          </button>
          {canManage && (
            <button
              onClick={() => navigate(`/project/${projectId}/team`)}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname.endsWith(`/team`)
                  ? 'text-white bg-white/10'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
              }`}
              title={sidebarCollapsed ? 'Team' : undefined}
            >
              <Users className="h-5 w-5" />
              {!sidebarCollapsed && <span className="ml-3">Team</span>}
            </button>
          )}
          {canViewActivity && (
            <button
              onClick={() => navigate(`/project/${projectId}/activity`)}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname.endsWith(`/activity`)
                  ? 'text-white bg-white/10'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
              }`}
              title={sidebarCollapsed ? 'Activity' : undefined}
            >
              <History className="h-5 w-5" />
              {!sidebarCollapsed && <span className="ml-3">Activity</span>}
            </button>
          )}
          <button
            onClick={() => navigate(`/project/${projectId}/help`)}
            className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
              onHelp
                ? 'text-white bg-white/10'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
            title={sidebarCollapsed ? 'Help' : undefined}
          >
            <HelpCircle className="h-5 w-5" />
            {!sidebarCollapsed && <span className="ml-3">Help</span>}
          </button>
          <button
            onClick={() => setSettingsPanelOpen(!settingsPanelOpen)}
            className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
              settingsPanelOpen
                ? 'text-white bg-white/10'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
            title={sidebarCollapsed ? 'Study Settings' : undefined}
          >
            <Settings className="h-5 w-5" />
            {!sidebarCollapsed && <span className="ml-3">Study Settings</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 rounded-lg transition-colors"
            title={sidebarCollapsed ? 'Logout' : undefined}
          >
            <LogOut className="h-5 w-5" />
            {!sidebarCollapsed && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar with breadcrumb */}
        <header className="h-16 bg-white/90 backdrop-blur-md border-b border-[#DCE2E5] flex items-center justify-between px-6 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center space-x-3">
            {/* Breadcrumb */}
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Projects
            </button>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
            <span className="text-sm text-gray-500 max-w-[200px] truncate">{projectName}</span>
            <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
            <h1 className="text-sm font-semibold text-gray-900">
              {headerTitle}
            </h1>
          </div>

          {/* Assistant button removed — the in-app chat helper isn't useful
              enough to justify the screen real estate yet. The chat
              backend (callable + onSnapshot listeners) remains in place
              for when we revisit. */}

          {/* Help — second entry point to the same guide the sidebar links to,
              so it's reachable from the top of every page. */}
          <button
            onClick={() => navigate(`/project/${projectId}/help`)}
            title="Help — how to use this platform"
            aria-label="Help"
            className={`h-8 w-8 flex items-center justify-center rounded-lg transition-colors ${
              onHelp
                ? 'text-primary-700 bg-primary-50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <HelpCircle className="h-[18px] w-[18px]" />
          </button>
        </header>

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden bg-[#F7F9FB]">
          {/* Main Content */}
          <main className={`flex-1 overflow-y-auto px-5 py-7 sm:px-8 lg:px-10 lg:py-9 ${settingsPanelOpen ? 'mr-96' : ''}`}>
            {children}
          </main>

          {/* Study Settings Panel */}
          {settingsPanelOpen && (
            <StudySetupPanel
              projectId={projectId}
              onClose={() => setSettingsPanelOpen(false)}
            />
          )}

          {/* AI Assistant Panel intentionally removed — see header comment. */}
        </div>
      </div>
    </div>
  );
}
